import { useMemo } from 'react';
import { SelectField } from '@understory-io/leaf/select-field';
import { Stack } from '@understory-io/leaf/stack';
import { useIntl } from 'react-intl';

import type {
  GiftCardExperienceOption,
  GiftCardGuests,
} from '~/domain/giftcard/experience';
import {
  TicketsSelector,
  type TicketSelectorVariant,
} from '~/ui/tickets-selector';

type Props = {
  experiences: GiftCardExperienceOption[];
  experienceId: string | null;
  guests: GiftCardGuests;
  onSelectExperience: (id: string) => void;
  onGuestsChange: (next: GiftCardGuests) => void;
  experienceError?: string;
  guestsError?: string;
};

/**
 * Experience gift-card controls: a Leaf `SelectField` to pick the experience,
 * then the ticket variants laid out inline via the shared `TicketsSelector` —
 * one raised box per variant, quantities always visible (no popover). Fully
 * controlled; the parent owns the selection and prices it.
 */
export function ExperienceFields({
  experiences,
  experienceId,
  guests,
  onSelectExperience,
  onGuestsChange,
  experienceError,
  guestsError,
}: Props) {
  const intl = useIntl();
  const selected = experiences.find((e) => e.id === experienceId) ?? null;

  const experienceLabel = intl.formatMessage({
    defaultMessage: 'Choose experience',
    description: 'Label for the experience gift card picker',
  });

  const options = experiences.map((e) => ({ value: e.id, label: e.name }));
  const onSelect = (next?: string) => {
    if (next) onSelectExperience(next);
  };

  // Gift cards aren't VAT-taxed at purchase, so every price (variant + add-on)
  // is shown VAT-exempt (no "excl. VAT" line), keyed by id like the booking flow.
  const ticketVariants: TicketSelectorVariant[] = useMemo(() => {
    if (!selected) return [];
    const toPrice = (priceCents: number) => ({
      vatInclusivePriceCents: priceCents,
      vatExclusivePriceCents: priceCents,
      currency: selected.currency,
      exponent: 2,
      vatExempt: true,
    });
    return selected.variants.map((variant) => ({
      id: variant.id,
      name: variant.name,
      price: toPrice(variant.priceCents),
      addOns: (variant.addOns ?? []).map((addOn) => ({
        id: addOn.id,
        name: addOn.name,
        price: toPrice(addOn.priceCents),
      })),
    }));
  }, [selected]);

  // Drop zero-quantity entries so the selection stays minimal.
  const handleQuantitiesChange = (next: Record<string, number>) => {
    const cleaned: GiftCardGuests = {};
    for (const [id, quantity] of Object.entries(next)) {
      if (quantity > 0) cleaned[id] = quantity;
    }
    onGuestsChange(cleaned);
  };

  return (
    <Stack gap="medium">
      {/* Leaf's FormValidation is a discriminated union (tone+message travel
          together), so the error and non-error fields are separate elements. */}
      {experienceError ? (
        <SelectField
          label={experienceLabel}
          options={options}
          value={experienceId ?? undefined}
          onChange={onSelect}
          tone="critical"
          message={experienceError}
        />
      ) : (
        <SelectField
          label={experienceLabel}
          options={options}
          value={experienceId ?? undefined}
          onChange={onSelect}
        />
      )}

      {ticketVariants.length > 0 && (
        <TicketsSelector
          variants={ticketVariants}
          quantities={guests}
          onQuantitiesChange={handleQuantitiesChange}
          errorMessage={guestsError}
        />
      )}
    </Stack>
  );
}
